/**
 * Form for editing profile fields (name, email).
 */

import { useState, type FormEvent } from 'react';
import type { ProfileUpdateRequest } from '@/types/auth';

interface ProfileFormProps {
  fullName: string;
  email: string;
  onSubmit: (payload: ProfileUpdateRequest) => Promise<void>;
}

export default function ProfileForm({ fullName, email, onSubmit }: ProfileFormProps) {
  const [name, setName] = useState(fullName);
  const [emailValue, setEmailValue] = useState(email);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!name.trim()) {
      setError('Name is required.');
      return;
    }

    setIsSubmitting(true);
    try {
      const updates: ProfileUpdateRequest = {};
      if (name.trim() !== fullName) updates.full_name = name.trim();
      if (emailValue.trim() !== email) updates.email = emailValue.trim();

      if (Object.keys(updates).length === 0) {
        setSuccess('No changes to save.');
        return;
      }

      await onSubmit(updates);
      setSuccess('Profile updated successfully.');
    } catch {
      setError('Failed to update profile.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {success && <div className="rounded-md bg-green-50 px-4 py-3 text-sm text-green-700">{success}</div>}

      <div>
        <label htmlFor="profile-name" className="block text-sm font-medium text-gray-700">Full Name</label>
        <input
          id="profile-name"
          className="input-field mt-1"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div>
        <label htmlFor="profile-email" className="block text-sm font-medium text-gray-700">Email</label>
        <input
          id="profile-email"
          type="email"
          className="input-field mt-1"
          value={emailValue}
          onChange={(e) => setEmailValue(e.target.value)}
        />
      </div>

      <div className="flex justify-end pt-2">
        <button type="submit" disabled={isSubmitting} className="btn-primary">
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
}
